import React, { useState, useCallback, memo, forwardRef } from 'react';
import LoadingSpinner from '../utils/LoadingSpinner';
import EmptyState from '../utils/EmptyState';
import JobDateGroup from './JobDateGroup';

// Define the component function with forwardRef to support receiving a ref prop
const JobsColumn = forwardRef(function JobsColumn({
  title = 'Jobs',
  jobsByDate = {},
  loading = false,
  badgeColor = 'bg-blue-100 text-blue-800',
  spinnerColor = 'border-sky-600',
  renderJobCard,
  emptyStateProps = {
    title: 'No jobs available',
    description: 'There are no jobs to display at this time.',
    icon: 'calendar'
  },
  containerClassName = '',
  contentClassName = '',
  defaultExpanded = true,
  totalCount,
  hasMore = false,
  jobsByDateTotals = {}, // Add prop for date-specific totals
  showCounter = true, // New prop to control counter visibility
  footerContent = null, // New prop for footer content
  handleLoadMore = () => {}, // Add handleLoadMore prop
  isLoadingMore = false, // Add isLoadingMore prop
  isAlwaysExpanded = false // New prop to disable accordion functionality
}, ref) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const hasJobs = Object.keys(jobsByDate).length > 0;

  // Calculate total jobs count from the jobsByDate object
  const jobsCount = Object.values(jobsByDate).reduce(
    (sum, jobs) => sum + jobs.length,
    0
  );

  // Use provided total count or calculate from jobs array
  const displayCount = totalCount !== undefined ? totalCount : jobsCount;

  // Always use fixed height for job list containers to make scrolling more stable
  const fixedContainerClassName = title === 'Jobs To Complete'
    ? 'h-[800px]' // Fixed height for the main jobs column
    : containerClassName;

  // Memoize toggle handler to prevent recreating on each render
  const handleToggle = useCallback((e) => {
    if (e.target.closest('button') || isAlwaysExpanded) return;
    setIsExpanded(prev => !prev);
  }, [isAlwaysExpanded]);

  // Handle keyboard events for accessibility
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!isAlwaysExpanded) {
        handleToggle(e);
      }
    }
  }, [handleToggle, isAlwaysExpanded]);

  // Memoize button click handler
  const handleButtonClick = useCallback((e) => {
    e.stopPropagation();
    if (!isAlwaysExpanded) {
      setIsExpanded(prev => !prev);
    }
  }, [isAlwaysExpanded]);
  
  // Stop scroll propagation to parent container
  const handleScrollPropagation = useCallback((e) => {
    // Prevent scroll from bubbling up to parent containers
    e.stopPropagation();
  }, []);

  // Ensure emptyStateProps has default values to prevent destructuring errors
  const safeEmptyStateProps = emptyStateProps || {
    title: 'No jobs available',
    description: 'There are no jobs to display at this time.',
    icon: 'calendar'
  };

  // If always expanded, force the expanded state
  const finalExpandedState = isAlwaysExpanded ? true : isExpanded;

  return (
    <div className={`bg-white rounded-lg shadow overflow-hidden ${finalExpandedState ? fixedContainerClassName : 'h-auto'}`}>
      <div 
        className={`px-4 sm:px-6 py-4 border-b border-gray-200 ${isAlwaysExpanded ? '' : 'cursor-pointer hover:bg-gray-50'} transition-colors duration-150`}
        onClick={handleToggle}
        role={isAlwaysExpanded ? undefined : "button"}
        tabIndex={isAlwaysExpanded ? undefined : 0}
        onKeyDown={handleKeyDown}
        aria-expanded={finalExpandedState}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">{title}</h2>
          <div className="flex items-center">
            {hasJobs && showCounter && (
              <span className={`flex items-center justify-center h-6 w-auto px-2 mr-2 text-xs font-medium ${badgeColor} rounded-full`}>
                {displayCount}
                {hasMore && (
                  <span className="ml-1 text-xs text-gray-500">+</span>
                )}
              </span>
            )}
            {!isAlwaysExpanded && (
              <button
                onClick={handleButtonClick}
                className="p-1 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                aria-expanded={finalExpandedState}
                aria-label={finalExpandedState ? 'Collapse section' : 'Expand section'}
              >
                <svg
                  className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${finalExpandedState ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
      <div 
        className={`p-2 sm:p-6 ${contentClassName} ${finalExpandedState ? '' : 'hidden'}`}
        id={title === 'Jobs To Complete' ? 'job-list-container' : ''}
        onScroll={title === 'Jobs To Complete' ? undefined : handleScrollPropagation}
        ref={title === 'Jobs To Complete' ? ref : undefined}
      >
        <div className="space-y-4 sm:space-y-8">
          {loading ? (
            <LoadingSpinner color={spinnerColor} />
          ) : !hasJobs ? (
            <EmptyState {...safeEmptyStateProps} />
          ) : (
            Object.entries(jobsByDate).map(([date, jobs]) => (
              <JobDateGroup
                key={date}
                date={date}
                jobs={jobs}
                renderJobCard={renderJobCard}
                badgeColor={badgeColor}
                jobsByDateTotals={jobsByDateTotals}
              />
            ))
          )}
        </div>
        
        {/* Footer content - Status messages, load more buttons, etc. */}
        {footerContent && finalExpandedState && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            {footerContent}
          </div>
        )}
      </div>
    </div>
  );
});

// Then export the memoized version
export default memo(JobsColumn); 